import React, { useState } from 'react';
import { Sparkles, RefreshCw, CheckCircle, MessageSquare, Edit2 } from 'lucide-react';

const Step4_QuestionSetPreview = ({ wizardData, updateData }) => {
  const { profile, strategicContext, questionSet } = wizardData;
  const [generating, setGenerating] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const generateQuestions = async () => {
    setGenerating(true);

    // Simulate question generation based on strategic context
    setTimeout(() => {
      const mockQuestions = [
        {
          id: 'q1',
          type: 'text',
          focus: '',
          question: `What academic subject or challenge have you encountered that felt surprisingly natural to you, even when others around you struggled with it?`,
          placeholder: 'Share your thoughts...',
          rationale: 'Explores natural abilities and learning patterns'
        },
        {
          id: 'q2',
          type: 'text',
          focus: '',
          question: `Describe a time when you had to advocate for yourself or take an unpopular position. What motivated you, and what did you learn?`,
          placeholder: 'Share your thoughts...',
          rationale: 'Assesses courage and values-driven decision making'
        },
        {
          id: 'q3',
          type: 'text',
          focus: '',
          question: `What project, activity, or idea have you pursued despite being told it was impractical or beyond your current level?`,
          placeholder: 'Share your thoughts...',
          rationale: 'Reveals initiative and growth mindset'
        },
        {
          id: 'q4',
          type: 'text',
          focus: '',
          question: `When have you brought an unexpected skill or perspective to a situation where others didn't see the connection?`,
          placeholder: 'Share your thoughts...',
          rationale: 'Identifies unique thinking patterns and problem-solving approaches'
        },
        {
          id: 'q5',
          type: 'text',
          focus: '',
          question: `What do you care deeply about that might not be obvious from your resume or transcript?`,
          placeholder: 'Share your thoughts...',
          rationale: 'Uncovers genuine interests and passions'
        },
        {
          id: 'q6',
          type: 'text',
          focus: '',
          question: `Describe a moment when a teacher, coach, or mentor underestimated or misunderstood your capabilities. How did you respond?`,
          placeholder: 'Share your thoughts...',
          rationale: 'Explores resilience and self-advocacy'
        },
        {
          id: 'q7',
          type: 'text',
          focus: '',
          question: `What systems, rules, or conventional approaches have you questioned? What alternative did you propose or implement?`,
          placeholder: 'Share your thoughts...',
          rationale: 'Assesses critical thinking and entrepreneurial mindset'
        },
        {
          id: 'q8',
          type: 'text',
          focus: '',
          question: `How do you approach learning something completely new, especially in an area where you have no prior experience or natural advantage?`,
          placeholder: 'Share your thoughts...',
          rationale: 'Reveals learning strategies and adaptability'
        }
      ];

      updateData('questionSet', mockQuestions);
      setGenerating(false);
    }, 2000);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questionSet];
    updated[index] = { ...updated[index], [field]: value };
    updateData('questionSet', updated);
  };

  const hasQuestions = questionSet && questionSet.length >= 8;

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Question Set Preview</h2>
        <p className="text-slate-600">
          Generate personalized assessment questions (1-8) based on {profile.name}&#39;s strategic context.
        </p>
      </div>

      {!hasQuestions && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Generate Assessment Questions</h3>
          <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
            Create 8 personalized questions based on the strategic context and personality profile.
          </p>
          <button
            onClick={generateQuestions}
            disabled={generating}
            className="px-8 py-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold text-lg disabled:opacity-50 flex items-center gap-3 mx-auto"
          >
            {generating ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Generating Questions...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Questions
              </>
            )}
          </button>
        </div>
      )}

      {hasQuestions && (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-emerald-900 mb-1">Questions Generated!</h4>
              <p className="text-sm text-emerald-700">
                Review and edit the questions below. Click on any question to modify it.
              </p>
            </div>
            <button
              onClick={generateQuestions}
              disabled={generating}
              className="text-emerald-600 hover:text-emerald-800 flex items-center gap-2 text-sm font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              Regenerate All
            </button>
          </div>

          <div className="space-y-4">
            {questionSet.map((q, index) => (
              <div key={index} className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-bold">
                      {index + 1}
                    </div>
                    <h4 className="font-semibold text-slate-900">Question {index + 1}</h4>
                  </div>
                  <button
                    onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                    className="text-indigo-600 hover:text-indigo-800 flex items-center gap-2 text-sm"
                  >
                    <Edit2 className="w-4 h-4" />
                    {editingIndex === index ? 'Done' : 'Edit'}
                  </button>
                </div>

                {editingIndex === index ? (
                  <textarea
                    value={q.question}
                    onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mb-3"
                  />
                ) : (
                  <p className="text-slate-700 mb-3 leading-relaxed">{q.question}</p>
                )}

                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-600">
                    <span className="font-medium">Rationale:</span> {q.rationale}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-azure-900 mb-1">Next Step: Generate Mock Answers</h4>
                <p className="text-sm text-azure-700">
                  AI will create realistic student responses to these questions based on the profile and parent insights.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step4_QuestionSetPreview;
